import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { API } from 'api';
import { ExternalGeoEvent } from 'shared/types';
import { useAuthState } from 'state/useAuthState';

type UseExternalGeoEvents = {
  externalGeoEvents: ExternalGeoEvent[];
  isExternalGeoEventFetchError: boolean;
  isExternalGeoEventLoading: boolean;
};

type UseExternalGeoEvent = {
  externalGeoEvent: ExternalGeoEvent | undefined;
  isExternalGeoEventFetchError: boolean;
  isExternalGeoEventLoading: boolean;
};

const EXTERNAL_GEO_EVENTS_URL = 'geo_events_external/';
const REFETCH_MS = 1000 * 20;

const fetchExternalGeoEvents = (): Promise<AxiosResponse<ExternalGeoEvent[]>> =>
  API.get<ExternalGeoEvent[]>(EXTERNAL_GEO_EVENTS_URL, {
    params: {
      lookbackMinutes: 30,
    },
  });

const fetchExternalGeoEvent = (
  id: string,
): Promise<AxiosResponse<ExternalGeoEvent>> =>
  API.get<ExternalGeoEvent>(`${EXTERNAL_GEO_EVENTS_URL}${id}`);

export const useExternalGeoEvents = (): UseExternalGeoEvents => {
  const {
    permissions: { isInternalUser },
  } = useAuthState();

  const { data, isError, isLoading } = useQuery({
    queryKey: ['externalGeoEvents'],
    queryFn: () => fetchExternalGeoEvents(),
    refetchInterval: REFETCH_MS,
    enabled: isInternalUser,
  });

  const results = useMemo(() => data?.data || [], [data]);

  return {
    externalGeoEvents: results,
    isExternalGeoEventFetchError: isError,
    isExternalGeoEventLoading: isLoading,
  };
};

export const useExternalGeoEvent = (
  id: string | undefined,
): UseExternalGeoEvent => {
  const {
    permissions: { isInternalUser },
  } = useAuthState();

  const { data, isError, isLoading } = useQuery({
    queryKey: ['externalGeoEvent', id],
    queryFn: () => fetchExternalGeoEvent(id!),
    refetchInterval: REFETCH_MS,
    enabled: isInternalUser && Boolean(id),
  });

  return {
    externalGeoEvent: data?.data,
    isExternalGeoEventFetchError: isError,
    isExternalGeoEventLoading: isLoading,
  };
};

export const useInvalidateExternalGeoEventQuery = (
  id: string | number,
): (() => Promise<void>) => {
  const queryClient = useQueryClient();

  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ['externalGeoEvent', String(id)],
      }),
    [queryClient, id],
  );
};
